import { Card,Row,Col,Button, Flex, Space, Table, Tag, Typography } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes, userRoles } from "../../../../../../constants";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate ,timeObjToDispViewTimeWithSuffix} from "../../../../../../utils/utils";
import { getCustomersAction } from "../../../../../../redux/services/customers/customersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import styles from "./CouponCodes.module.scss";
import { getCouponCodesAction } from "../../../../../../redux/services/coupon-codes/couponCodesServices";
import { colors } from "../../../../../../constants/colors";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";
import { DeleteIcon, EditIcon } from "../../../../../../assets/svg";
import Actions from "../../../../../../redux/actions";
import CouponCodeDeleteDialog from "./CouponCodeDeleteDialog";
import { TagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();
const CouponCodes = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { couponCodes } = useSelector((state) => state.couponCodes);
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: couponCodes?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openCouponCodeDeleteDialog, setOpenCouponCodeDeleteDialog] =
    useState(false);
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    setLoading(true);
    const dataToBeSent = {
      page: pagination?.pageNo,
      limit: pagination?.pageSize,
    };
    dispatch(getCouponCodesAction(dataToBeSent, setLoading)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination]);


  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.CouponCodes.code"),
        dataIndex: "coupon_code",
        key: "coupon_code",
      },
      {
        title: t("Modules.CouponCodes.couponName"),
        dataIndex: "coupon_name",
        key: "coupon_name",
      },
      {
        title: t("Modules.CouponCodes.couponCategory"),
        key: "coupon_category",
        dataIndex: "coupon_category",
        align: "center",
        render: (_, { coupon_category }) => (
          <Tag
            key={coupon_category[0]}
            style={{
              background:
                coupon_category[0] === "ORDER"
                  ? colors.slatePurple
                  : colors.darkSearGreen,
              color:
                coupon_category[0] === "ORDER"
                  ? colors.indigo
                  : colors.limeGreen,
            }}
            bordered={false}
          >
            {coupon_category[0]}
          </Tag>
        ),
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.discountType"),
        dataIndex: "discount_type",
        key: "discount_type",
      },
      {
        title: t("Modules.Packages.PackagesForm.discount"),
        dataIndex: "discount",
        render: (_, record) => (
          <p>
            {record?.discount_type === "PERCENTAGE"
              ? `${record?.discount_percent}%`
              : `${record?.discount_amount}`}
          </p>
        ),
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.startDate"),
        dataIndex: "start",
        key: "start",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.start, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.expiryDate"),
        dataIndex: "expiry",
        key: "expiry",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.expiry, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("couponUsed"),
        dataIndex: "is_used",
        key: "is_used",
        render: (_, { is_used }) => (
          <Tag
            key={is_used}
            style={{
              background: is_used
                ? colors.lightPastelBlue
                : colors.darkGoldenRod,
              color: is_used ? colors.electricBlue : colors.mango,
            }}
            bordered={false}
          >
            {is_used ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        title: t("appointmentInfo.status"),
        dataIndex: "is_active",
        key: "is_active",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case true:
                return { background: colors.lightTeal, color: colors.darkTeal };
  
              case false:
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };
  
              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.is_active),
                  textTransform: "capitalize",
                }}
              >
                {record?.is_active === true ? t("CommonUtils.active") : t("CommonUtils.inactive")}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("createAt"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{`${dateObjToDispViewDate(record?.created_at, "ddmmyyyy")} - ${timeObjToDispViewTimeWithSuffix(dayjs
              .utc(record?.created_at).local().tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ss")
          )}`}</p>
        ),
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
            {
              record?.is_active ?
              <Flex gap="small" justify="center">
                {(
                  <Space size="middle">
                    <Button
                      type="text"
                      size="medium"
                      icon={<EditIcon fillColor={colors.slateGray} />}
                      onClick={(e) => handleAddEditDetails(e, record)}
                    />
                  </Space>
                )}
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<DeleteIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleConfirmDelete(e, record)}
                  />
                </Space>
              </Flex>
              :
              <>-</>
            }
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_COUPON_CODE_ID, {
        selectedCouponCodeId: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.ADD_COUPON_CODES));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCouponCodeId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_COUPON_CODE_ID, {
          selectedCouponCodeId,
        })
      );
    }
  };

  const handleConfirmDelete = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_COUPON_CODE_ID, {
        selectedCouponCodeId: "",
      })
    );
    setOpenCouponCodeDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCouponCodeId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_COUPON_CODE_ID, {
          selectedCouponCodeId,
        })
      );
    }
  };

  return (
    <div className={styles.couponCodesContainer}>
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<TagOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("SignUp.couponCodes")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${couponCodes?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
          </Card>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={(e) => handleAddEditDetails(e, null)}
          >
            {t("Modules.CouponCodes.addCouponCode")}
          </Button>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={couponCodes?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
      <CouponCodeDeleteDialog
        open={openCouponCodeDeleteDialog}
        onClose={() => {
          setOpenCouponCodeDeleteDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default CouponCodes;
